import { useCallback, useMemo, useState } from 'react';
import { MarketHero } from '@/components/MarketHero';
import { QuickNav } from '@/components/QuickNav';
import { SwipeStage } from '@/components/SwipeStage';
import { SectionHeader } from '@/components/SectionHeader';
import { StarCard } from '@/components/StarCard';
import { PremiumCard } from '@/components/PremiumCard';
import { PerksList } from '@/components/PerksList';
import { CardSkeleton } from '@/components/Skeleton';
import { PurchaseSheet } from '@/components/PurchaseSheet';
import { SuccessOverlay } from '@/components/SuccessOverlay';
import { useProducts } from '@/hooks/useProducts';
import { useOrders } from '@/hooks/useOrders';
import { useBottomSheet } from '@/hooks/useBottomSheet';
import { useTelegram } from '@/hooks/useTelegram';
import { isStarProduct, isPremiumProduct } from '@/types/product';
import type { Product } from '@/types/product';
import type { RecipientInfo } from '@/types/order';
import styles from './Market.module.scss';

// Order defines both the QuickNav's left-to-right layout AND which
// direction the swipe animates when jumping between two sections —
// e.g. featured -> premium swipes further than featured -> stars.
const SECTION_ORDER = ['featured', 'stars', 'premium'] as const;
type SectionKey = (typeof SECTION_ORDER)[number];

const QUICK_NAV_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'stars', label: 'Stars' },
  { value: 'premium', label: 'Premium' },
];

interface SuccessInfo {
  title: string;
  message: string;
}

function recipientLabel(recipient: RecipientInfo): string {
  return recipient.type === 'gift' ? `Gift to @${recipient.username}` : 'For yourself';
}

export function MarketPage() {
  const { products, loading, error } = useProducts();
  const { placeOrder } = useOrders();
  const bottomSheet = useBottomSheet();
  const { hapticImpact, hapticNotification, hapticSelect } = useTelegram();

  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null);

  const [activeSection, setActiveSection] = useState<SectionKey>('featured');
  const [direction, setDirection] = useState<1 | -1>(1);

  const featured = useMemo(() => products.filter((p) => p.tags?.includes('featured')), [products]);
  const popular = useMemo(() => products.filter((p) => p.tags?.includes('popular')), [products]);
  const stars = useMemo(() => products.filter(isStarProduct), [products]);
  const premium = useMemo(() => products.filter(isPremiumProduct), [products]);

  const handleSelect = useCallback(
    (product: Product) => {
      hapticImpact('light');
      setActiveProduct(product);
      bottomSheet.open();
    },
    [bottomSheet, hapticImpact],
  );

  const handleConfirm = useCallback(
    async (recipient: RecipientInfo) => {
      if (!activeProduct || isSubmitting) return;
      setIsSubmitting(true);
      hapticNotification('success');
      try {
        await placeOrder(activeProduct, recipient);
        bottomSheet.close();
        setSuccessInfo({
          title: 'Purchase complete',
          message: `${activeProduct.title} — ${recipientLabel(recipient)}.`,
        });
      } catch {
        // The mock API never fails; a real backend would surface an
        // inline error in the sheet here instead of closing it.
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeProduct, isSubmitting, placeOrder, bottomSheet, hapticNotification],
  );

  const handleSectionChange = useCallback(
    (value: string) => {
      const nextKey = value as SectionKey;
      if (nextKey === activeSection) return;
      hapticSelect();
      const nextIndex = SECTION_ORDER.indexOf(nextKey);
      const currentIndex = SECTION_ORDER.indexOf(activeSection);
      setDirection(nextIndex > currentIndex ? 1 : -1);
      setActiveSection(nextKey);
    },
    [activeSection, hapticSelect],
  );

  return (
    <div className={styles.page}>
      <MarketHero />
      <QuickNav options={QUICK_NAV_OPTIONS} active={activeSection} onChange={handleSectionChange} />

      {error && <p className={styles.error}>{error}</p>}

      <SwipeStage activeKey={activeSection} direction={direction}>
        {activeSection === 'featured' && (
          <>
            <section className={styles.section}>
              <SectionHeader title="Featured" subtitle="Hand-picked for you" />
              <div className={styles.scrollRow} role="list" aria-busy={loading}>
                {loading
                  ? Array.from({ length: 2 }).map((_, i) => <CardSkeleton key={i} variant="featured" />)
                  : featured.map((product) =>
                      isStarProduct(product) ? (
                        <StarCard key={product.id} product={product} variant="featured" onSelect={handleSelect} />
                      ) : (
                        <PremiumCard key={product.id} product={product} variant="featured" onSelect={handleSelect} />
                      ),
                    )}
              </div>
            </section>

            <section className={styles.section}>
              <SectionHeader title="Popular" subtitle="Trending this week" />
              <div className={styles.scrollRow} role="list" aria-busy={loading}>
                {loading
                  ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} variant="featured" />)
                  : popular.map((product) =>
                      isStarProduct(product) ? (
                        <StarCard key={product.id} product={product} variant="featured" onSelect={handleSelect} />
                      ) : (
                        <PremiumCard key={product.id} product={product} variant="featured" onSelect={handleSelect} />
                      ),
                    )}
              </div>
            </section>
          </>
        )}

        {activeSection === 'stars' && (
          <section className={styles.section}>
            <SectionHeader title="Stars" subtitle="Send, tip, and unlock features" />
            <div className={styles.grid} role="list" aria-busy={loading}>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
                : stars.map((product) => <StarCard key={product.id} product={product} onSelect={handleSelect} />)}
            </div>
          </section>
        )}

        {activeSection === 'premium' && (
          <section className={styles.section}>
            <SectionHeader title="Telegram Premium" subtitle="Unlock the full experience" />
            <div className={styles.planList} role="list" aria-busy={loading}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
                : premium.map((product) => <PremiumCard key={product.id} product={product} onSelect={handleSelect} />)}
            </div>
            {!loading && <PerksList />}
          </section>
        )}
      </SwipeStage>

      <PurchaseSheet
        product={activeProduct}
        bottomSheet={bottomSheet}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirm}
      />
      <SuccessOverlay
        isOpen={!!successInfo}
        title={successInfo?.title ?? ''}
        message={successInfo?.message ?? ''}
        onDone={() => setSuccessInfo(null)}
      />
    </div>
  );
}
